import React, { useContext, useState, useRef, useEffect } from 'react';
import { WorkspaceContext } from '../../contexts/WorkspaceContext';
import './AISection.css';

function AISection({ selectedId, onSelect }) {
  const {
    aiConversations,
    unreadCounts,
    createAIConversation,
    archiveConversation,
    renameConversation,
    toggleFavorite
  } = useContext(WorkspaceContext);

  const [collapsed, setCollapsed] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const editRef = useRef(null);
  const menuRef = useRef(null);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.select();
    }
  }, [editingId]);

  // Close context menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpenId]);

  const resolveTitle = (conv) => {
    const title = (conv.title || '').trim();
    if (!title || title === 'Untitled Conversation' || title === 'New Conversation') {
      return 'New AI chat';
    }
    return title;
  };

  const startEditing = (conv) => {
    setMenuOpenId(null);
    setEditingId(conv.id);
    setEditValue(resolveTitle(conv));
  };

  const commitRename = async (convId, currentTitle) => {
    if (isRenaming) return;
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === currentTitle) {
      setEditingId(null);
      return;
    }
    setIsRenaming(true);
    try {
      await renameConversation(convId, trimmed);
      setEditingId(null);
    } catch {
      // close edit mode so failed mutations do not trap the rename input
      setEditingId(null);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleEditKeyDown = (e, convId, currentTitle) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename(convId, currentTitle);
    } else if (e.key === 'Escape') {
      setEditingId(null);
    }
  };

  const handleArchive = (conv) => {
    setMenuOpenId(null);
    const name = resolveTitle(conv);
    if (window.confirm(`Delete "${name}"? This cannot be undone.`)) {
      archiveConversation(conv.id);
    }
  };

  const handleFavorite = (conv) => {
    setMenuOpenId(null);
    toggleFavorite(conv.id);
  };

  const handleNewAIChat = () => {
    createAIConversation('New AI chat');
  };

  return (
    <div className="ai-section">
      <div className="section-header">
        <button
          type="button"
          className="section-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          <svg
            className={`section-chevron ${collapsed ? 'collapsed' : ''}`}
            viewBox="0 0 16 16"
            width="10"
            height="10"
            fill="currentColor"
          >
            <path d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708l-3 3a.5.5 0 01-.708 0l-3-3a.5.5 0 010-.708z" />
          </svg>
          <span className="section-label">AI Assistant</span>
        </button>
        <button
          className="section-add-btn"
          onClick={handleNewAIChat}
          aria-label="New AI chat"
          title="New AI chat"
        >
          +
        </button>
      </div>
      {!collapsed && (
        <div className="section-list">
          {aiConversations.map((conv) => {
            const displayName = resolveTitle(conv);
            const unread = unreadCounts[conv.id] || 0;
            const isEditing = editingId === conv.id;

            return (
              <div
                key={conv.id}
                className={`ai-item-row ${selectedId === conv.id ? 'selected' : ''}`}
              >
                {isEditing ? (
                  <div className="ai-item ai-item-editing">
                    <span className="ai-dot" />
                    <input
                      ref={editRef}
                      className="ai-edit-input"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, conv.id, displayName)}
                      onBlur={() => commitRename(conv.id, displayName)}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`ai-item ${unread > 0 ? 'ai-item-unread' : ''}`}
                    onClick={() => onSelect(conv)}
                    aria-pressed={selectedId === conv.id}
                  >
                    <span className="ai-dot" />
                    <span className="ai-name">{displayName}</span>
                    {unread > 0 && (
                      <span className="unread-badge">{unread > 99 ? '99+' : unread}</span>
                    )}
                  </button>
                )}
                {!isEditing && (
                  <div className="ai-item-actions" style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="item-context-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === conv.id ? null : conv.id);
                      }}
                      aria-label={`Actions for ${displayName}`}
                      title="More actions"
                    >
                      <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
                        <path d="M3 9.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm5 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
                      </svg>
                    </button>
                    {menuOpenId === conv.id && (
                      <div className="context-menu" ref={menuRef}>
                        <button
                          type="button"
                          className="context-menu-item"
                          onClick={() => handleFavorite(conv)}
                        >
                          Add to Favorites
                        </button>
                        <button
                          type="button"
                          className="context-menu-item"
                          onClick={() => startEditing(conv)}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          className="context-menu-item context-menu-item-danger"
                          onClick={() => handleArchive(conv)}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {aiConversations.length === 0 && (
            <span className="section-empty">No AI chats yet</span>
          )}
        </div>
      )}
    </div>
  );
}

export default AISection;
